import { useEffect, useContext, useRef, useState } from 'react';
import AuthContext from 'src/store/auth-context';
import AdminAuthContext from 'src/store/admin-auth-context';
import { AppContext } from 'src/store/app-context';

const useAutoLogout = () => {
  // Accessing authentication contexts
  const adminAuthContext = useContext(AdminAuthContext);
  const authContext = useContext(AuthContext);
  const { setAutoLogoutPath } = useContext(AppContext);

  const [isLoggedInAsAdmin] = useState(adminAuthContext.isLoggedIn);

  // Reference to manage the logout timer
  const logoutTimerRef = useRef();

  // Determining the appropriate logout function based on the current authentication status
  const [logout] = useState(() => {
    if (adminAuthContext.isLoggedIn && authContext.isLoggedIn) {
      return () => {
        adminAuthContext.logout({ enableNotification: false });
        authContext.logout({ enableNotification: true }); //enable only one of the notficiations so it does not show duplicate lout toast notifications
      };
    } else if (adminAuthContext.isLoggedIn) {
      return () => adminAuthContext.logout();
    } else if (authContext.isLoggedIn) {
      return () => authContext.logout();
    }
  });

  // Calculate the remaining time before auto-logout
  const [remainingTime] = useState(() => {
    return adminAuthContext.isLoggedIn ? adminAuthContext.remainingTime : authContext.remainingTime;
  });

  // Setting up and managing the auto-logout timer
  useEffect(() => {
    // Dynamically setting the logout path based on authentication context
    setAutoLogoutPath(() => (isLoggedInAsAdmin ? '/admin/login' : '/login'));

    if (remainingTime) {
      // Clear any existing timer to prevent multiple instances
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

      // Setting up the auto-logout timer
      logoutTimerRef.current = setTimeout(logout, remainingTime);

      // Cleanup: Clear the timer on unmount or when dependencies change
      return () => clearTimeout(logoutTimerRef.current);
    }
  }, [
    adminAuthContext.logout,
    authContext.logout,
    remainingTime,
    logout,
    setAutoLogoutPath,
    isLoggedInAsAdmin,
  ]);
};

export default useAutoLogout;
